/**
 * PORTED — RO-3-00c / #44470 (dependency of issues-title-toolbar).
 * Origin: `excellent/app/_modes/work-issues-subnav.tsx`.
 *
 * Changes:
 *   next/navigation → `@excellent/ui/router` (useNavigate / usePathname / useSearch)
 *   Seam navigate is Next-shaped {push,replace,…}, not TanStack callable.
 */

import { DisplayMenu, NavTabs, type NavTab } from "@excellent/ui";
import { List, Kanban, CalendarDays } from "@excellent/ui/icons";
import { useNavigate, usePathname, useSearch } from "@excellent/ui/router";
import {
  GROUPING_OPTIONS,
  ORDERING_OPTIONS,
  PROPERTY_OPTIONS,
  issuesDisplayParams,
  resolveIssuesDisplay,
  type IssueGrouping,
  type IssueOrdering,
  type IssueProperty,
} from "@/work/issues-display";

/** Trailing path segments that name a VIEW of the Issues section. */
const ISSUE_VIEW_SEGS = new Set(["tasks", "board", "planner"]);

/**
 * Issues section view toggle — List · Board · Calendar. Emits canonical section
 * paths (`/work/issues`, `/work/issues/board`, `/work/issues/planner`).
 */
export function IssuesSubNav({ bare = false }: { bare?: boolean } = {}) {
  const pathname = usePathname() || "/";
  const navigate = useNavigate();
  // Seam useSearch returns URLSearchParams (not TanStack parsed object).
  const sp = useSearch();

  // Recover the Issues SECTION base (`/work/issues`) from wherever we're mounted.
  const parts = pathname.split("/").filter(Boolean);
  const leaf = parts[parts.length - 1] ?? "";
  const hasIssuesSeg = parts.includes("issues");
  let base: string;
  if (hasIssuesSeg) {
    const segs = ISSUE_VIEW_SEGS.has(leaf) ? parts.slice(0, -1) : parts;
    base = "/" + segs.join("/");
  } else {
    base = pathname === "/" ? "/work/issues" : `${pathname}/issues`;
  }

  const views: NavTab[] = [
    { href: base, label: "List", activeHref: base },
    { href: `${base}/board`, label: "Board" },
    { href: `${base}/planner`, label: "Calendar" },
  ];

  if (bare) {
    const onBoard = leaf === "board";
    const onPlanner = leaf === "planner";
    const value = onBoard ? "board" : onPlanner ? "planner" : "list";
    const href: Record<string, string> = {
      list: base,
      board: `${base}/board`,
      planner: `${base}/planner`,
    };
    const display = resolveIssuesDisplay((k) => sp.get(k));
    const set = (next: Parameters<typeof issuesDisplayParams>[0]) => {
      const q = new URLSearchParams(sp.toString());
      for (const [k, v] of Object.entries(issuesDisplayParams(next))) {
        if (v == null) q.delete(k);
        else q.set(k, v);
      }
      const qs = q.toString();
      navigate.replace(qs ? `${pathname}?${qs}` : pathname);
    };

    return (
      <DisplayMenu
        views={{
          options: [
            { value: "list", label: "List", icon: List },
            { value: "board", label: "Board", icon: Kanban },
            { value: "planner", label: "Calendar", icon: CalendarDays },
          ],
          value,
          onChange: (v) => navigate.push(href[v] ?? base),
        }}
        sections={[
          {
            id: "organize",
            controls: [
              {
                kind: "select",
                id: "grouping",
                label: "Grouping",
                options: GROUPING_OPTIONS,
                value: display.grouping,
                onChange: (v) => set({ grouping: v as IssueGrouping }),
              },
              {
                kind: "select",
                id: "ordering",
                label: "Ordering",
                options: ORDERING_OPTIONS,
                value: display.ordering,
                onChange: (v) => set({ ordering: v as IssueOrdering }),
              },
              {
                kind: "switch",
                id: "empty",
                label: "Show empty groups",
                checked: display.showEmpty,
                onChange: (on) => set({ showEmpty: on }),
                disabled: display.grouping !== "status",
              },
            ],
          },
          {
            id: "list",
            label: "List options",
            controls: [
              {
                kind: "chips",
                id: "properties",
                label: "Display properties",
                options: PROPERTY_OPTIONS,
                values: display.properties,
                onChange: (v) => set({ properties: v as IssueProperty[] }),
              },
            ],
          },
        ]}
      />
    );
  }

  return (
    <div className="border-b border-border/40">
      <NavTabs tabs={views} className="gap-4" />
    </div>
  );
}
